// Helpers for US-stocks portfolio assets (asset_type "us_stocks" with a
// stock_holdings array, an uninvested cash_usd, and invested_inr — the
// total INR spent converting to USD).
//
// Ledger portfolios (asset.ledger) are derived from entries instead: each
// holding carries a broker, broker_cash splits cash_usd per broker, and
// invested_inr is the net ₹ converted.
import { formatCurrency, formatUSD } from "../constants";

const usd2 = (v) => formatUSD(v, { decimals: 2, suffix: "" });

/** "3 holdings at 2 brokers + $12.35 cash · $4,963.02" one-line summary. */
export function stockSummary(asset) {
  const n = new Set((asset?.stock_holdings || []).map((h) => h.symbol)).size;
  const parts = [`${n} holding${n === 1 ? "" : "s"}`];
  const brokers = asset?.ledger ? ledgerBrokers(asset).length : 0;
  if (brokers > 1) parts[0] += ` at ${brokers} brokers`;
  if (asset?.cash_usd > 0) {
    parts[0] += ` + $${Number(asset.cash_usd).toLocaleString("en-US", { maximumFractionDigits: 2 })} cash`;
  }
  if (asset?.total_value_usd > 0) {
    parts.push(`$${Number(asset.total_value_usd).toLocaleString("en-US", { maximumFractionDigits: 2 })}`);
  }
  return parts.join(" · ");
}

/**
 * True INR-basis gain: today's INR value vs. the INR actually spent
 * converting to USD. Captures market and FX movement together; null when
 * invested_inr isn't set.
 */
export function stockGainINRPercent(asset) {
  const invested = Number(asset?.invested_inr);
  const current = Number(asset?.current_value);
  if (!(invested > 0) || !(current > 0)) return null;
  return ((current - invested) / invested) * 100;
}

/** Brokers a ledger portfolio knows about (server order first), matched
 *  case-insensitively like the server does. */
export function ledgerBrokers(asset, transactions = []) {
  const names = [];
  const add = (name) => {
    if (name && !names.some((n) => n.toLowerCase() === name.toLowerCase())) names.push(name);
  };
  (asset?.broker_cash || []).forEach((c) => add(c.broker));
  (asset?.stock_holdings || []).forEach((h) => add(h.broker));
  transactions.forEach((t) => {
    add(t.broker);
    add(t.to_broker);
  });
  return names;
}

/** Per-broker view of a ledger portfolio: [{ broker, cash, holdings, usd }]. */
export function holdingsByBroker(asset) {
  return ledgerBrokers(asset).map((broker) => {
    const holdings = (asset.stock_holdings || []).filter((h) => h.broker === broker);
    const cash = (asset.broker_cash || []).find((c) => c.broker === broker)?.usd || 0;
    const usd = holdings.reduce((sum, h) => sum + h.units * (h.last_price || 0), cash);
    return { broker, cash, holdings, usd };
  });
}

/** Units without float noise: 2.5, 0.123457. */
export function formatUnits(units) {
  return String(Number((Number(units) || 0).toFixed(6)));
}

/** ₹ per $ of a conversion entry. */
export function entryRate(tx) {
  return tx.usd_amount > 0 ? tx.inr_amount / tx.usd_amount : null;
}

const ENTRY_LABELS = { convert: "Convert", buy: "Buy", sell: "Sell", transfer: "Transfer" };

/**
 * An entry as the activity list shows it: badge label, title, a subline
 * (where · details · note) and the signed $ effect on cash.
 */
export function describeEntry(tx) {
  const sub = (...parts) => parts.filter(Boolean).join(" · ");
  const label = ENTRY_LABELS[tx.type] || tx.type;
  switch (tx.type) {
    case "convert": {
      const rate = entryRate(tx);
      const rateText = rate ? `₹${rate.toFixed(2)}/$` : null;
      return tx.direction === "to_inr"
        ? {
            label,
            title: `Converted ${usd2(tx.usd_amount)} → ${formatCurrency(tx.inr_amount)}`,
            sub: sub(tx.bank ? `${tx.broker} → ${tx.bank}` : tx.broker, rateText, tx.note),
            amount: `−${usd2(tx.usd_amount)}`,
          }
        : {
            label,
            title: `Converted ${formatCurrency(tx.inr_amount)} → ${usd2(tx.usd_amount)}`,
            sub: sub(tx.bank ? `${tx.bank} → ${tx.broker}` : tx.broker, rateText, tx.note),
            amount: `+${usd2(tx.usd_amount)}`,
          };
    }
    case "buy":
      return {
        label,
        title: `Bought ${formatUnits(tx.units)} ${tx.symbol} @ ${usd2(tx.price_usd)}`,
        sub: sub(tx.broker, tx.fee_usd > 0 && `incl. ${usd2(tx.fee_usd)} fee`, tx.note),
        amount: `−${usd2(tx.units * tx.price_usd + (tx.fee_usd || 0))}`,
      };
    case "sell": {
      const gain = tx.realized_usd;
      return {
        label,
        title: `Sold ${formatUnits(tx.units)} ${tx.symbol} @ ${usd2(tx.price_usd)}`,
        sub: sub(
          tx.broker,
          tx.fee_usd > 0 && `after ${usd2(tx.fee_usd)} fee`,
          gain != null && `${gain >= 0 ? "+" : "−"}${usd2(Math.abs(gain))} vs avg cost`,
          tx.note
        ),
        amount: `+${usd2(tx.units * tx.price_usd - (tx.fee_usd || 0))}`,
      };
    }
    case "transfer": {
      const moved = (tx.items || []).map((it) => `${formatUnits(it.units)} ${it.symbol}`);
      if (tx.usd_amount > 0) moved.push(usd2(tx.usd_amount));
      return {
        label,
        title: `Moved ${moved.join(", ")}`,
        sub: sub(`${tx.broker} → ${tx.to_broker}`, tx.fee_usd > 0 && `${usd2(tx.fee_usd)} fee`, tx.note),
        amount: tx.usd_amount > 0 ? usd2(tx.usd_amount) : "",
      };
    }
    default:
      return { label, title: tx.type, sub: tx.broker, amount: "" };
  }
}
